import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { debounce, isString, unset } from 'lodash'
import { useSelector } from 'react-redux'

import { getAllInternships } from '../../../../utils/api/internship'
import { DATE_WITHOUT_TIME } from '../../../../utils/constants'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import useLocalStorageOld from '../../../../hooks/UseLocalStorage'
import InternshipsTable from './InternshipsTable'
import InternshipProvider from '../../../../Context/InternshipsManager/InternshipProvider'
import InternshipsManagerProvider from '../../../../Context/InternshipsManager/InternshipsManagerProvider'
import { DEFAULT_INTERNSHIP_FILTERS, GlobalFiltersContext } from '../../../../Providers/GlobalFiltersProvider'
import LOCAL_STORAGE_KEYS from '../../../../utils/localStorage'
import { getStudentFreeFields } from '../../../../utils/api/studentFreeField'
import { selectUser } from '../../../../reducers/UserReducer'
import { retrieveMetadata } from '../../../../utils/http'
import { API_DATE, getTimezoneLessMoment } from '../../../../utils/momentjs'
import { isNonEmptyArray } from '../../../../utils'

const { INTERNSHIPS_MANAGER } = LOCAL_STORAGE_KEYS

const InternshipsListView = () => {
  const { internshipDate, internshipFilters, internshipSearch, setInternshipDate, setInternshipFilters, setInternshipSearch } = useContext(GlobalFiltersContext)
  const { areStartedExcluded, institutions, schools, sections, states: status, weeksShownCount, ownership } = internshipFilters

  const user = useSelector(selectUser)

  const [internships, setInternships] = useState([])
  const [loading, setLoading] = useState(false)
  const [rawParameters, setRawParameters] = useLocalStorageOld(LOCAL_STORAGE_KEYS.INTERNSHIPS_MANAGER.PARAMETERS, { order: { by: 'startDate', direction: 'desc', pageSize: 10 } })

  const filters = useMemo(() => (
    { areStartedExcluded, institutions, schools, sections, status, weeksShownCount, ownership, search: internshipSearch }
  ), [areStartedExcluded, institutions, schools, sections, status, weeksShownCount, ownership, internshipSearch])
  const period = useMemo(() => (
    { startDate: internshipDate, endDate: internshipDate.clone().add(weeksShownCount ?? 12, 'month') }
  ), [internshipDate.format(DATE_WITHOUT_TIME), weeksShownCount])
  const parameters = useMemo(() => new TableParameters(
    { ...rawParameters, filters: filters, period: period }
  ), [rawParameters, filters, period])

  useEffect(() => {
    const fetch = debounce(() => {
      setLoading(true)

      fetchInternships(new TableParameters({ ...rawParameters, filters, period }))
    }, 300)

    fetch()

    return () => {
      fetch.cancel()
    }
  }, [filters, period, rawParameters.page, rawParameters.pageSize, rawParameters.order?.by, rawParameters.order?.direction])

  const fetchInternships = useCallback(parameters => {
    const formattedParameters = parameters.toQueryParameters()

    formattedParameters.startDate = parameters.period.startDate
    formattedParameters.endDate = parameters.period.endDate
    formattedParameters.contexts = ['coordinator', 'school']
    formattedParameters.states = parameters.filters.status

    if (isString(parameters.filters.search)) {
      formattedParameters.search = formattedParameters.filters.search
    }

    unset(formattedParameters, 'filters[states]')

    getAllInternships(user, formattedParameters).then(({ data, meta }) => {
      if (data) {
        const metadata = retrieveMetadata(meta)
        setInternships(data)
        setRawParameters(prev => new TableParameters({ ...prev, pageSize: metadata.pageSize, totalPages: metadata.totalPages, metadata }))
        getFreeFields(data)
      }
    }).finally(() => setLoading(false))
  }, [user])

  const getFreeFields = useCallback((internships) => {
    if (isNonEmptyArray(internships)) {
      getStudentFreeFields(user, internships.map(i => i.student?.id ?? null).filter(i => i !== null)).then(({ data }) => {
        const newData = internships.map(internship => {
          const studentId = internship.student?.id ?? -1
          const freeFields = data[studentId]
          return {
            ...internship,
            student: {
              ...internship.student,
              freeFields: freeFields || {}
            }
          }
        })
        setInternships(newData)
      })
    }
  }, [])

  const handleParametersChange = useCallback(newParameters => {
    setInternshipSearch(newParameters.filters.search)
    setInternshipDate(newParameters.period.startDate)

    if (
      newParameters.filters.search !== parameters.filters.search ||
      getTimezoneLessMoment(newParameters.period.startDate).format(API_DATE) !== getTimezoneLessMoment(parameters.period.startDate).format(API_DATE)
    ) {
      setRawParameters(prev => ({ ...prev, ...newParameters, page: 1 }))
    } else {
      setRawParameters(prev => ({ ...prev, ...newParameters }))
    }
  }, [parameters])

  const handleFilterChange = () => {
    setRawParameters({ ...rawParameters, page: 1 })
  }

  const handleResetFilters = () => {
    setRawParameters({ ...rawParameters, page: 1 })
    setInternshipSearch('')
    setInternshipFilters(DEFAULT_INTERNSHIP_FILTERS)
  }

  return (
    <InternshipsManagerProvider>
      <InternshipProvider refreshQuotas={() => fetchInternships(parameters)}>
        <InternshipsTable
          data={internships}
          loading={loading}
          parameters={parameters}
          storageKey={`${INTERNSHIPS_MANAGER.LIST_VIEW_TABLE_DISPLAY}-${user.id}`}
          onParametersChange={handleParametersChange}
          onDataChange={fetchInternships}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />
      </InternshipProvider>
    </InternshipsManagerProvider>

  )
}

export default InternshipsListView
